import { css } from 'styled-components';

import { pickColor } from 'themes/helpers/pickColor';
import { mediaHover } from 'themes/mixins/mediaHover';

const motives = {
  m0: null,
  m1: css`
    letter-spacing: 0.1rem;
    text-transform: uppercase;

    font-weight: 300;

    ${mediaHover(css`
      color: ${pickColor('red')};
    `)}
  `,
  navItem: css`
    display: flex;
    align-items: center;

    padding: 1rem 2rem;

    color: ${pickColor('white')};

    text-transform: uppercase;

    span {
      font-size: 1.8rem;
    }

    ${mediaHover(css`
      background-color: ${pickColor('redTransparent')};
    `)}
  `,
  tableAction: css`
    justify-content: center;

    padding: 0.4rem 1.2rem;

    color: ${pickColor('white')};
    background-color: ${pickColor('black')};
    border: 0.1rem solid ${pickColor('black')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    ${mediaHover(css`
      background-color: ${pickColor('borderColorDarkSecondary')};
      border-color: ${pickColor('borderColorDarkSecondary')};
    `)}
  `,
  // Same fixed 3rem square footprint as tableActionDelete (that one has no
  // horizontal padding to size around, being icon-only and border-only) —
  // tableAction's own padding-based sizing (0.4rem 1.2rem, no fixed
  // width/height) is fine for its text-label buttons elsewhere, but made an
  // icon-only View/Edit button visibly wider than the Delete button next to
  // it (reported live: the trash icon was narrower).
  tableActionView: css`
    justify-content: center;
    align-items: center;

    width: 3rem;
    height: 3rem;
    padding: 0;

    color: ${pickColor('white')};
    background-color: ${pickColor('black')};
    border: 0.1rem solid ${pickColor('black')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    ${mediaHover(css`
      background-color: ${pickColor('borderColorDarkSecondary')};
      border-color: ${pickColor('borderColorDarkSecondary')};
    `)}
  `,
  tableActionSecondary: css`
    padding: 0.4rem 1.2rem;

    border: 0.1rem solid ${pickColor('black')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    ${mediaHover(css`
      background-color: ${pickColor('borderColorDarkSecondary')};
      color: ${pickColor('white')};
      border-color: ${pickColor('borderColorDarkSecondary')};
    `)}
  `,

  primaryBlack: css`
    justify-content: center;

    padding: 0.5rem 2rem;
    min-width: 4rem;

    color: ${pickColor('white')};
    background-color: ${pickColor('black')};
    border: 0.1rem solid ${pickColor('black')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    text-transform: uppercase;

    ${mediaHover(css`
      background-color: ${pickColor('borderColorDarkSecondary')};
      border-color: ${pickColor('borderColorDarkSecondary')};
    `)}
  `,
  secondaryBlack: css`
    justify-content: center;

    padding: 0.5rem 2rem;
    min-width: 4rem;

    color: ${pickColor('black')};
    border: 0.1rem solid ${pickColor('black')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    text-transform: uppercase;

    ${mediaHover(css`
      color: ${pickColor('white')};
      background-color: ${pickColor('borderColorDarkSecondary')};
      border-color: ${pickColor('borderColorDarkSecondary')};
    `)}
  `,
  m4: css`
    height: 3rem;
    padding: 0 2.2rem;
    width: fit-content;

    background-color: ${pickColor('black')};
    color: ${pickColor('white')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    text-transform: capitalize;

    span {
      margin-top: 0;
    }
  `,
  tableActionDelete: css`
    justify-content: center;
    align-items: center;

    width: 3rem;
    height: 3rem;
    padding: 0;

    background-color: transparent;
    color: ${pickColor('black')};
    border: 0.1rem solid ${pickColor('borderColorDark')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    ${mediaHover(css`
      color: ${pickColor('red')};
      border-color: ${pickColor('red')};
    `)}
  `,
  m5: css`
    font-weight: 400;

    ${mediaHover(css`
      color: ${pickColor('red')};
    `)}
  `,
  accept: css`
    min-height: 4rem;
    padding: 0 2.2rem;

    background-color: ${pickColor('colmarBlue')};
    color: ${pickColor('white')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    text-transform: uppercase;
    justify-content: center;

    ${mediaHover(css`
      background-color: ${pickColor('colmarRedSecondary')};
    `)}

    span {
      font-size: 1.8rem;
    }
  `,
  addBlue: css`
    padding: 0.6rem 1.4rem;

    border-radius: ${({ theme }) => theme.borderRadius.base};
    background-color: ${pickColor('blue')};
    color: ${pickColor('white')};

    ${mediaHover(css`
      background-color: ${pickColor('blueDark')};
    `)}
  `,
  delete: css`
    min-height: 4rem;
    padding: 0 2.2rem;

    background-color: ${pickColor('colmarBlueSecondaryButton')};
    color: ${pickColor('colmarBlue')};
    border-radius: ${({ theme }) => theme.borderRadius.big};

    text-transform: uppercase;
    justify-content: center;

    ${mediaHover(css`
      background-color: ${pickColor('black')};
      color: ${pickColor('white')};
    `)}

    span {
      font-size: 1.8rem;
    }
  `,
  add: css`
    min-height: 4rem;
    padding: 0 2.2rem;

    background-color: ${pickColor('blue')};
    color: ${pickColor('white')};
    border-radius: ${({ theme }) => theme.borderRadius.big};

    text-transform: uppercase;

    ${mediaHover(css`
      background-color: ${pickColor('blueDark')};
    `)}
  `,
  navSubItem: css`
    text-transform: uppercase;

    font-weight: 300;

    color: ${pickColor('white')};

    cursor: pointer;

    padding-left: 3rem;
    margin: 0.2rem 0.5rem;

    &.active {
      color: ${pickColor('white')};
      background-color: ${pickColor('colmarRed')};
      border-radius: ${({ theme }) => theme.borderRadius.big};
    }
  `,
  round: css`
    padding: 1rem;

    background-color: ${pickColor('white')};
    border-radius: 50%;
    box-shadow: 0.4rem 0.4rem 0.9rem -0.2rem rgba(66, 68, 90, 1);

    ${mediaHover(css`
      background-color: ${pickColor('blue')};
    `)}
  `,
  submit: css`
    padding: 0.6rem 1.4rem;

    background-color: ${pickColor('orange')};
    color: ${pickColor('white')};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    ${mediaHover(css`
      background-color: ${pickColor('greenDark')};
    `)}
  `,
  back: css`
    display: flex;
    align-items: top;
    text-transform: uppercase;

    span {
      font-size: 1.8rem;
    }
  `,
  filter: css`
    justify-content: center;

    height: 3.5rem;

    padding: 0 4rem;

    background-color: ${pickColor('colmarBlue')};
    color: ${pickColor('white')};
    border-radius: 0.3rem;

    text-transform: uppercase;

    ${mediaHover(css`
      background-color: ${pickColor('colmarRed')};
    `)}

    span {
      font-size: 1.6rem;
    }
  `,
  filterSecondary: css`
    justify-content: center;

    height: 3.5rem;

    padding: 0.8rem 2rem;

    border: 0.1rem solid ${pickColor('colmarBlue')};
    background: ${pickColor('white')};
    color: ${pickColor('colmarBlue')};
    border-radius: 0.3rem;

    text-transform: uppercase;

    span {
      font-size: 1.6rem;
    }

    ${mediaHover(css`
      background-color: ${pickColor('black')};
      color: ${pickColor('white')};
    `)}
  `,
  addNew: css`
    box-sizing: border-box;
    justify-content: center;
    height: 4rem;

    padding: 0.6rem 3rem;

    background-color: ${pickColor('colmarRed')};
    color: ${pickColor('white')};
    border-radius: 0.5rem;

    text-transform: uppercase;

    span {
      font-size: 1.6rem;

      svg {
        stroke-width: 3;
      }
    }

    ${mediaHover(css`
      background-color: ${pickColor('colmarRedSecondary')};
    `)}
  `,
  pagination: css`
    padding: 0.5rem 1.6rem;

    border: 0.1rem solid ${pickColor('borderColorDarkSecondary')};
    background-color: ${pickColor('white')};
    border-radius: ${({ theme }) => theme.borderRadius.big};

    ${mediaHover(css`
      background-color: ${pickColor('colmarBlue')};
      color: ${pickColor('white')};
    `)};
  `,
  red: css`
    color: ${pickColor('red')};

    ${mediaHover(css`
      color: ${pickColor('black')};
    `)}
  `,
  white: css`
    color: ${pickColor('white')};

    span {
      font-size: 2.1rem;
    }
  `,
  send: css`
    display: flex;
    justify-content: center;
    align-items: center;

    width: 3.6rem;
    height: 3.6rem;

    background-color: ${pickColor('colmarBlue')};
    color: ${pickColor('white')};
    border-radius: 50%;

    span {
      font-size: 2.4rem;
    }
  `,
  blackAccept: css`
    justify-content: center;

    min-height: 4.5rem;

    padding: 1.15rem 0;

    background-color: ${pickColor('black')};

    color: ${pickColor('white')};

    border-radius: ${({ theme }) => theme.borderRadius.base};

    text-transform: uppercase;

    font-weight: 700;

    font-family: 'LFT Zeno Condensed', sans-serif;

    ${mediaHover(css`
      background-color: ${pickColor('colmarRedSecondary')};
    `)}
  `,
  forgotPassword: css`
    color: ${pickColor('colmarRedSecondary')};

    ${mediaHover(css`
      color: ${pickColor('black')};
    `)}
  `,
  backSecondary: css`
    svg {
      stroke-width: 2;
    }

    ${mediaHover(css`
      color: ${pickColor('colmarRed')};
    `)};
  `,
  carouselNavigation: css`
    display: flex;
    justify-content: center;
    align-items: center;

    height: 6.5rem;
    width: 4.5rem;

    background-color: ${pickColor('white')};
    box-shadow: 0 0.3rem 2.4rem -1rem ${pickColor('shadow')};

    ${mediaHover(css`
      color: ${pickColor('blue')};
    `)};
  `,
  deleteIcon: css`
    color: ${pickColor('red')};

    ${mediaHover(css`
      color: ${pickColor('white')};
    `)}
  `,
};

export type ButtonMotives = keyof typeof motives;

const pickMotive = (motive: ButtonMotives) => motives[motive];

export { pickMotive };
